import { PortalAccessProfile } from "types/api";

export type SportsWorkspaceMode = "global" | "school" | "assigned";

export type SportsWorkspaceContent = {
  mode: SportsWorkspaceMode;
  pageDescription: string;
  listTitle: string;
  listSubtitle: string;
  emptyDescription: string;
  emptyTitle: string;
  profileDescription: string;
  profileOverviewSubtitle: string;
  showFeaturedSection: boolean;
  showProfileNetworkSections: boolean;
};

export const getSportsWorkspaceMode = (
  portalAccess?: PortalAccessProfile | null,
  canManageSports = false,
): SportsWorkspaceMode => {
  if (canManageSports || portalAccess?.scope?.global || portalAccess?.portal_type === "admin") {
    return "global";
  }

  if (portalAccess?.portal_type === "school_management") {
    return portalAccess?.capabilities?.can_view_assigned_sport_only ? "assigned" : "school";
  }

  return "global";
};

export const getSportsWorkspaceContent = (
  portalAccess?: PortalAccessProfile | null,
  canManageSports = false,
): SportsWorkspaceContent => {
  const mode = getSportsWorkspaceMode(portalAccess, canManageSports);

  if (mode === "assigned") {
    return {
      mode,
      pageDescription:
        "Reference the sports linked to your active school assignments without exposing the wider platform catalogue.",
      listTitle: "Assigned Sports",
      listSubtitle: "Browse the sports currently attached to the schools and programmes you oversee.",
      emptyDescription:
        "Assigned sports will appear here once your school role is linked to an active school sport.",
      emptyTitle: "No assigned sports match these filters",
      profileDescription:
        "Review the core metadata for this assigned sport. Team and athlete network details stay in the school workspace.",
      profileOverviewSubtitle: "Assigned sport metadata",
      showFeaturedSection: false,
      showProfileNetworkSections: false,
    };
  }

  if (mode === "school") {
    return {
      mode,
      pageDescription:
        "Reference the sports currently active across the schools you manage without exposing the full platform catalogue.",
      listTitle: "School Sports",
      listSubtitle: "Browse the sports currently available across your active school assignments.",
      emptyDescription:
        "School-linked sports will appear here once your assignments are connected to active school sports.",
      emptyTitle: "No school sports match these filters",
      profileDescription:
        "Review the core metadata for this school-linked sport. Team and athlete network details stay in the school workspace.",
      profileOverviewSubtitle: "School-linked sport metadata",
      showFeaturedSection: false,
      showProfileNetworkSections: false,
    };
  }

  return {
    mode,
    pageDescription:
      "Define the competition categories that power teams, athletes, news, and marketplace context.",
    listTitle: "All Sports",
    listSubtitle: "Browse and manage sport records in a more visual layout.",
    emptyDescription: "Create your first sport to start structuring teams and athletes.",
    emptyTitle: "No sports match these filters",
    profileDescription:
      "Review the sport overview, related teams, and athlete participation from one screen.",
    profileOverviewSubtitle: "Primary sport metadata",
    showFeaturedSection: true,
    showProfileNetworkSections: true,
  };
};
